import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { AppState } from "../../store/definitions";
import { updateCsat } from "../../store/actions/genericActions";
import { getPendingCsat, submitCsat } from "../../csatHandler";
import { sessionDataHandler } from "../../sessionDataHandler";
import { isConversationEnded } from "../../store/selectors";

/*
 * Orquestração do CSAT (dados + rede), separada da apresentação (CsatCard) — SRP/testabilidade.
 * Ao montar (na despedida), consulta o synccx (GET /pending) e semeia chat.csat; expõe `submit()`
 * que faz o POST e dirige a máquina de status. Best-effort (o csatHandler nunca lança/loga PII).
 */
export function useCsat() {
    const dispatch = useDispatch();
    const config = useSelector((state: AppState) => state.config.csat);
    const survey = useSelector((state: AppState) => state.chat.csat);
    const conversationSid = useSelector((state: AppState) => state.chat.conversation?.sid);
    /*
     * ONDE a pessoa está no momento do envio — lido aqui, e não deduzido na despedida.
     *
     * Com o disparo por macro o mesmo POST bem-sucedido passa a ter dois significados: respondeu
     * durante o atendimento, ou respondeu na despedida. Depois do envio os dois são indistinguíveis
     * (`answered: true`, `status: "submitted"` nos dois), então a informação só existe agora.
     */
    const conversaEncerrada = useSelector((state: AppState) => isConversationEnded(state.chat));
    const submittingRef = useRef(false); // guard síncrono contra duplo-submit (S6)

    // Mount: pergunta a pesquisa pendente e semeia o store uma vez (se ainda não semeado).
    useEffect(() => {
        if (!config?.enabled || survey) {
            return undefined;
        }
        let active = true;
        getPendingCsat({
            conversationSid,
            csatKey: sessionDataHandler.getCsatKey(),
            // Decide se a desistência vira aviso à operação: no atendimento é defeito, na despedida pode ser o normal.
            noAtendimento: !conversaEncerrada,
            aindaImporta: () => active
        }).then((seed) => {
            if (active && seed) {
                /*
                 * A ORIGEM da resposta, derivada — ela não vem no fio, e não sobrevive ao reload.
                 *
                 * `respondidaNoAtendimento` nasce no submit e morre ao recarregar a página. Um seed
                 * que chega JÁ RESPONDIDO com a conversa ainda viva só pode ter sido respondido
                 * durante o atendimento: na despedida a conversa já estaria encerrada. Sem isto, quem
                 * respondeu no meio e recarregou reencontrava a pergunta na despedida quando a
                 * pesquisa aceita edição — o gate `answered && !allowEdit` deixava a escala passar.
                 */
                const respondidaNoAtendimento = seed.answered && !conversaEncerrada ? true : undefined;
                dispatch(
                    updateCsat({ ...seed, status: "idle", ...(respondidaNoAtendimento ? { respondidaNoAtendimento } : {}) })
                );
            }
        });
        return () => {
            active = false;
        };
    }, [config?.enabled, survey, conversationSid, conversaEncerrada, dispatch]);

    const submit = useCallback(
        async (score: number, comment: string) => {
            if (submittingRef.current || !survey) {
                return;
            }
            submittingRef.current = true;
            dispatch(updateCsat({ status: "submitting" }));

            const result = await submitCsat({
                conversationSid,
                csatKey: sessionDataHandler.getCsatKey(),
                score,
                comment: config?.appearance.comment.enabled ? comment : undefined,
                scale: survey.scale
            });

            submittingRef.current = false;

            if (result === "submitted") {
                /*
                 * Os TRÊS desfechos do POST levam o SID. Eles são o que pode aterrissar depois de o
                 * apostador já ter aberto outra conversa — o "Iniciar nova conversa" fica clicável
                 * enquanto o envio está em voo, e só o botão Enviar respeita o `busy`.
                 */
                dispatch(
                    updateCsat(
                        {
                            status: "submitted",
                            answered: true,
                            score,
                            comment,
                            respondidaNoAtendimento: !conversaEncerrada
                        },
                        conversationSid
                    )
                );
            } else if (result === "readonly") {
                /*
                 * `readonly` também é resposta dada, e a origem conta igual. O caso que motiva: o POST
                 * grava, a resposta se perde, o retry leva 409 e chega aqui. O widget agradece na
                 * faixa — e sem marcar a origem, a despedida repetia o agradecimento de algo que a
                 * pessoa fez minutos antes.
                 */
                dispatch(
                    updateCsat({ status: "readonly", respondidaNoAtendimento: !conversaEncerrada }, conversationSid)
                );
            } else {
                // error/noop transitório: volta ao respondível (não trava a despedida).
                dispatch(updateCsat({ status: "idle" }, conversationSid));
            }
        },
        [conversationSid, config, survey, conversaEncerrada, dispatch]
    );

    /*
     * A faixa do atendimento já mostrou o agradecimento e pode sair da tela.
     *
     * Quem chama é o card, por timer, depois de a mensagem ter ficado tempo suficiente visível — e
     * não o backend do synccx, que desliga `csatPending` no mesmo instante em que grava a nota e
     * por isso apagava o agradecimento recém-renderizado. O widget não pode perder um estado
     * terminal que ele mesmo acabou de produzir.
     *
     * Leva o SID pelo mesmo motivo dos desfechos do POST: o timer pode disparar depois de o
     * apostador já ter aberto outra conversa, e aí a guarda de procedência do reducer descarta.
     */
    const aposentarFaixa = useCallback(() => {
        dispatch(updateCsat({ faixaAposentada: true }, conversationSid));
    }, [conversationSid, dispatch]);

    return { config, survey, submit, aposentarFaixa };
}
